/**
 * Airport search for Gravity Forms
 *
 * Autocomplete for the airport search field rendered by lib/gravity-forms.php
 * (Surface Ops "Airport Coverage"). The airport list is fetched from the REST
 * endpoint on first focus and filtered in the browser. Each pick becomes a
 * box below the input holding a hidden uavionix_airports_{id}[] code, and a
 * row in the linked Gravity Forms List field (added / removed with the box).
 * PHP rewrites the List rows from the boxes on submit as a backstop.
 */
import '../styles/airport-search.scss';
import './choose-plan';
import './required-fields';
import './eula';

const MAX_RESULTS = 50;

// CSV "Size" column; bigger airports sort first within a match rank.
const SIZE_ORDER = { L: 0, M: 1, S: 2, Nonhub: 3, GA: 4 };
let airportsRequest = null;

/**
 * Fetch the airport list once per page, shared by every field.
 */
const loadAirports = (url) => {
  if (!airportsRequest) {
    airportsRequest = fetch(url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Airport list failed to load (${response.status})`);
        }
        return response.json();
      })
      .then((rows) => rows.map(([code, airport, city, state, size, flightline, flightlineCode]) => ({
        code,
        airport,
        // Must match uavionix_airport_label() in PHP.
        label: `${code} ${airport} - ${city}, ${state}`,
        size: SIZE_ORDER[size] ?? 5,
        sizeCode: size ?? '',
        flightline: flightline ?? '', // Yes / No / SWIM
        flightlineCode: flightlineCode ?? '',
        // Full CSV row, keyed by header, for the List field cells.
        csv: {
          Code: code, Airport: airport, City: city, State: state, FlightLine: flightlineCode ?? '', Size: size ?? '',
        },
        place: [city, state].filter(Boolean).join(', '),
        haystack: ` ${code} ${airport} ${city} ${state}`.toLowerCase(),
      })))
      .catch((error) => {
        airportsRequest = null;
        throw error;
      });
  }
  return airportsRequest;
};

/**
 * Rank airports for a query: exact code, code prefix, word prefix, anywhere,
 * then by airport size. Every space-separated term must match somewhere.
 */
const searchAirports = (airports, query, exclude) => {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) {
    return [];
  }

  const first = terms[0];
  const matches = [];

  airports.forEach((airport) => {
    if (exclude.has(airport.code) || !terms.every((term) => airport.haystack.includes(term))) {
      return;
    }

    const code = airport.code.toLowerCase();
    let rank = 3;
    if (code === first) {
      rank = 0;
    } else if (code.startsWith(first)) {
      rank = 1;
    } else if (airport.haystack.includes(` ${first}`)) {
      rank = 2;
    }
    matches.push({ airport, rank });
  });

  return matches
    .sort((a, b) => a.rank - b.rank || a.airport.size - b.airport.size)
    .slice(0, MAX_RESULTS)
    .map((match) => match.airport);
};

const createElement = (tag, className, text) => {
  const el = document.createElement(tag);
  if (className) {
    el.className = className;
  }
  if (text !== undefined) {
    el.textContent = text;
  }
  return el;
};

/**
 * List field cells for one airport: one per column (columns are CSV headers,
 * from PHP), or for a single-column List one line with every CSV value.
 * Mirrors uavionix_airport_list_cells() in PHP.
 */
const listCells = (airport, columns) => {
  if (!columns.length) {
    return [`${airport.label} | FlightLine: ${airport.csv.FlightLine} | Size: ${airport.csv.Size}`];
  }
  return columns.map((header) => airport.csv[header] ?? '');
};

/**
 * Selected airport box. Mirrors uavionix_airport_item_html() in PHP.
 */
const createItem = (airport, name, cells) => {
  const item = createElement('li', 'airport-search__item');
  item.dataset.code = airport.code;
  item.dataset.cells = JSON.stringify(cells);

  const hidden = createElement('input');
  hidden.type = 'hidden';
  hidden.name = name;
  hidden.value = airport.code;

  const remove = createElement('button', 'airport-search__remove', '×');
  remove.type = 'button';
  remove.setAttribute('aria-label', `Remove ${airport.code}`);

  const code = createElement('span', 'airport-search__code');
  const icon = createElement('i', 'fa-regular fa-plane-departure');
  icon.setAttribute('aria-hidden', 'true');
  code.append(icon, createElement('span', 'airport-search__code-text', airport.code));

  // Line 1: code + airport name.
  const heading = createElement('span', 'airport-search__heading');
  heading.append(code, createElement('span', 'airport-search__name', airport.airport));

  // Line 2: city/state.
  const location = createElement('span', 'airport-search__location');
  const place = createElement('span', 'airport-search__place', airport.place);
  const pin = createElement('i', 'fa-solid fa-location-dot');
  pin.setAttribute('aria-hidden', 'true');
  place.prepend(pin);
  location.append(place);

  // Line 3: "Size: [badge]" and "FlightLine: [Yes / No / SWIM badge]".
  const detail = (className, label, value) => {
    const el = createElement('span', className);
    el.append(createElement('span', 'airport-search__meta-label', label), ' ', value);
    return el;
  };
  const meta = createElement('span', 'airport-search__meta');
  if (airport.sizeCode) {
    const badge = createElement('span', 'airport-search__badge', airport.sizeCode);
    badge.dataset.size = airport.sizeCode;
    meta.append(detail('airport-search__size', 'Size:', badge));
  }
  if (airport.flightlineCode) {
    const flightline = detail('airport-search__flightline', 'FlightLine:', createElement('span', 'airport-search__badge airport-search__flightline-text', airport.flightline));
    flightline.dataset.flightline = airport.flightlineCode;
    meta.append(flightline);
  }

  item.append(hidden, heading, location, meta);
  item.append(remove);
  return item;
};

/**
 * Keep a Gravity Forms List field in step with the boxes, one row per
 * airport (single or multiple columns). Rows are added / removed with GF's
 * own Add / Remove buttons so its row limit, odd/even classes and aria
 * labels stay correct.
 */
const listSync = (listField) => {
  const rows = () => (listField ? [...listField.querySelectorAll('.gfield_list_group')] : []);
  const inputs = (row) => [...row.querySelectorAll('input, textarea, select')];
  const matches = (row, cells) => inputs(row).every((el, i) => el.value === (cells[i] ?? ''));
  const isEmpty = (row) => inputs(row).every((el) => el.value.trim() === '');
  const setValues = (row, cells) => {
    inputs(row).forEach((el, i) => {
      el.value = cells[i] ?? '';
      el.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  return {
    add(cells) {
      if (!listField || rows().some((row) => matches(row, cells))) {
        return;
      }

      let row = rows().find(isEmpty);
      if (!row) {
        const last = rows().pop();
        const addButton = last && last.querySelector('.add_list_item');
        if (!addButton || addButton.disabled || addButton.classList.contains('gfield_icon_disabled')) {
          return; // List is at its max rows; PHP still saves every airport.
        }
        addButton.click();
        row = rows().pop();
      }
      setValues(row, cells);
    },

    remove(cells) {
      const row = rows().find((r) => matches(r, cells));
      if (!row) {
        return;
      }
      // GF keeps at least one row, so clear the last one instead of deleting it.
      if (rows().length === 1) {
        setValues(row, []);
      } else {
        row.querySelector('.delete_list_item').click();
      }
    },
  };
};

/**
 * Set a GF field's input (found by the field's CSS class) and fire change so
 * conditional logic re-runs. No-op if the form has no such field.
 */
const setFieldValue = (form, selector, value) => {
  const input = form && form.querySelector(`${selector} input`);
  if (input && input.value !== value) {
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }
};

/**
 * Recount the form's airport counter fields from the page, so adds and removes
 * can't drift out of step:
 * - `.airport-counter`: search boxes plus unlisted airports
 * - `.unlisted-counter`: filled-in rows of the `.unlisted-airports` List
 *   (only while conditional logic shows it)
 * - `.flightline-counter`: search boxes with FlightLine Y
 */
const updateCounters = (form) => {
  if (!form) {
    return;
  }

  const items = [...form.querySelectorAll('.airport-search__item')];
  const flightline = items.filter((item) => {
    const el = item.querySelector('.airport-search__flightline');
    return el && el.dataset.flightline === 'Y';
  }).length;

  const unlisted = form.querySelector('.gfield.unlisted-airports');
  const unlistedOpen = !!unlisted && window.getComputedStyle(unlisted).display !== 'none';

  // The unlisted List takes over as the required airport field while it's
  // open, so hide the search field's required asterisk (airport-search.scss).
  form.querySelectorAll('.gfield.populate-airports').forEach((field) => {
    field.classList.toggle('is-unlisted-open', unlistedOpen);
  });

  const typed = unlistedOpen
    ? [...unlisted.querySelectorAll('.gfield_list_group')]
      .filter((row) => [...row.querySelectorAll('input, textarea, select')].some((el) => el.value.trim() !== ''))
      .length
    : 0;

  setFieldValue(form, '.airport-counter', String(items.length + typed));
  setFieldValue(form, '.unlisted-counter', String(typed));
  setFieldValue(form, '.flightline-counter', String(flightline));
};

const initAirportSearch = (container) => {
  if (container.dataset.ready) {
    return;
  }
  container.dataset.ready = '1';

  const input = container.querySelector('.airport-search__input');
  const dropdown = container.querySelector('.airport-search__dropdown');
  const results = container.querySelector('.airport-search__results');
  const selected = container.querySelector('.airport-search__selected');
  const { source, field } = container.dataset;
  const name = `uavionix_airports_${field}[]`;
  const list = listSync(document.getElementById(container.dataset.list));
  const listColumns = JSON.parse(container.dataset.listColumns || '[]');

  let airports = [];
  let options = [];
  let active = -1;

  const selectedCodes = () => new Set(
    [...selected.querySelectorAll('.airport-search__item')].map((item) => item.dataset.code),
  );

  const updateCount = () => {
    selected.dataset.count = selected.children.length;
    updateCounters(container.closest('form'));
  };

  const close = () => {
    dropdown.hidden = true;
    results.replaceChildren();
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    options = [];
    active = -1;
  };

  const setActive = (index, scroll = true) => {
    const items = results.querySelectorAll('[role="option"]');
    if (!items.length) {
      return;
    }
    active = (index + items.length) % items.length;
    items.forEach((item, i) => item.setAttribute('aria-selected', i === active ? 'true' : 'false'));
    if (scroll) {
      items[active].scrollIntoView({ block: 'nearest' });
    }
    input.setAttribute('aria-activedescendant', items[active].id);
  };

  const showMessage = (text) => {
    results.replaceChildren(createElement('li', 'airport-search__message', text));
    dropdown.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  const render = () => {
    const query = input.value.trim();
    if (!query) {
      close();
      return;
    }
    if (!airports.length) {
      showMessage('Loading airports…');
      return;
    }

    options = searchAirports(airports, query, selectedCodes());
    active = -1;
    input.removeAttribute('aria-activedescendant');

    if (!options.length) {
      showMessage('No airport matches');
      return;
    }

    results.replaceChildren(...options.map((airport, i) => {
      const option = createElement('li', 'airport-search__option');
      option.id = `${input.id}_option_${i}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.dataset.index = i;
      option.append(
        createElement('span', 'airport-search__code', airport.code),
        createElement('span', 'airport-search__name', airport.airport),
        createElement('span', 'airport-search__place', airport.place),
      );
      return option;
    }));
    dropdown.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    setActive(0);
  };

  const choose = (airport) => {
    if (!airport) {
      return;
    }
    const cells = listCells(airport, listColumns);
    selected.append(createItem(airport, name, cells));
    list.add(cells);
    updateCount();
    input.value = '';
    close();
    input.focus();
  };

  const ensureLoaded = () => {
    loadAirports(source)
      .then((list) => {
        airports = list;
        render();
      })
      .catch(() => {
        if (input.value.trim()) {
          showMessage('Airports could not be loaded. Please try again.');
        }
      });
  };

  // Warm the list once the page is idle so the first search is instant.
  (window.requestIdleCallback || ((cb) => setTimeout(cb, 1500)))(ensureLoaded);
  input.addEventListener('focus', ensureLoaded, { once: true });
  input.addEventListener('input', () => {
    if (!airports.length) {
      ensureLoaded();
    }
    render();
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (dropdown.hidden) {
        render();
      } else {
        setActive(active + (event.key === 'ArrowDown' ? 1 : -1));
      }
    } else if (event.key === 'Enter') {
      // Never submit the form from the search box.
      event.preventDefault();
      if (!dropdown.hidden && active > -1) {
        choose(options[active]);
      }
    } else if (event.key === 'Escape') {
      close();
    }
  });

  // mousedown (not click) so the pick lands before the input's blur closes the
  // list. Bound to the whole dropdown so clicks in its padding or on the
  // scrollbar don't blur the input either.
  dropdown.addEventListener('mousedown', (event) => {
    const option = event.target.closest('[role="option"]');
    event.preventDefault();
    if (option) {
      choose(options[Number(option.dataset.index)]);
    }
  });

  // Hovering moves the active row, so mouse and arrow keys share one highlight.
  results.addEventListener('mouseover', (event) => {
    const option = event.target.closest('[role="option"]');
    if (option && Number(option.dataset.index) !== active) {
      setActive(Number(option.dataset.index), false);
    }
  });

  input.addEventListener('blur', close);

  selected.addEventListener('click', (event) => {
    const remove = event.target.closest('.airport-search__remove');
    if (remove) {
      const item = remove.closest('.airport-search__item');
      list.remove(JSON.parse(item.dataset.cells || '[]'));
      item.remove();
      updateCount();
      input.focus();
    }
  });
};

const initAll = () => {
  document.querySelectorAll('.airport-search').forEach(initAirportSearch);
  document.querySelectorAll('.airport-search').forEach((container) => updateCounters(container.closest('form')));
};

document.addEventListener('DOMContentLoaded', initAll);

// Typing into (or clearing) an unlisted airport row.
document.addEventListener('input', (event) => {
  if (event.target.closest('.gfield.unlisted-airports')) {
    updateCounters(event.target.form);
  }
});

// The unlisted List's Cancel button (lib/gravity-forms.php) unticks "My airport
// is not listed". A real click on the checkbox runs GF's conditional logic, which
// hides the List again; focus then goes back to the checkbox it revealed.
document.addEventListener('click', (event) => {
  const cancel = event.target.closest('.unlisted-airports__cancel');
  const trigger = cancel && document.getElementById(cancel.dataset.unlistedTrigger);
  if (!trigger) return;

  if (trigger.checked) trigger.click();
  trigger.focus();
});

// Deleting an unlisted row with GF's Remove button fires no input event.
// Registered once GF's scripts (which define window.gform) have loaded.
document.addEventListener('DOMContentLoaded', () => {
  if (window.gform && window.gform.addAction) {
    window.gform.addAction('gform_list_post_item_delete', (field) => {
      updateCounters(field[0] && field[0].closest('form'));
    });
  }
});

if (window.jQuery) {
  // Re-run after AJAX submits / multi-page navigation re-render the form.
  window.jQuery(document).on('gform_post_render', initAll);

  // The unlisted List only counts while "My airport is not listed" shows it.
  window.jQuery(document).on('gform_post_conditional_logic', (event, formId) => {
    updateCounters(document.getElementById(`gform_${formId}`));
  });
}
